import React from "react";
import Tooltip from "@mui/material/Tooltip";
import { IconInfo } from "../../../utils/icons";
import { useUser } from "../../../hooks/useUser";
import { isInternal } from "../../../utils";

// ---------------------------------------------------------------------------
// Human-readable explanation generator
// ---------------------------------------------------------------------------

function joinHuman(list) {
    if (!list || list.length === 0) return "";
    if (list.length === 1) return list[0];
    if (list.length === 2) return `${list[0]} and ${list[1]}`;
    return `${list.slice(0, -1).join(", ")}, and ${list[list.length - 1]}`;
}

function formatYears(years) {
    if (!Number.isFinite(years) || years < 0) return "no recorded tenure";
    if (years < 1 / 12) return "less than a month";
    if (years < 1) {
        const months = Math.round(years * 12);
        return months === 1 ? "1 month" : `${months} months`;
    }
    const rounded = Math.round(years * 10) / 10;
    if (rounded === 1) return "1 year";
    return `${rounded} years`;
}

function pickPrimaryProfession(professions) {
    if (!professions || professions.length === 0) return null;
    const ranked = [...professions].sort((a, b) => {
        const specA = (a.subSubProfessionName ? 2 : 0) + (a.subProfessionName ? 1 : 0);
        const specB = (b.subSubProfessionName ? 2 : 0) + (b.subProfessionName ? 1 : 0);
        if (specA !== specB) return specB - specA;
        return (b.years || 0) - (a.years || 0);
    });
    return ranked[0];
}

function professionLabel(p) {
    if (!p) return "their profession";
    return (
        p.subSubProfessionName ||
        p.subProfessionName ||
        p.professionName ||
        "their profession"
    );
}

function attrMismatchSentence(name, productTitle, o) {
    const expert = o.expertValues.length ? joinHuman(o.expertValues) : "no values set";
    const productSide = o.productValues.length ? joinHuman(o.productValues) : "no values set";
    if (o.attrSlug === "age-group") {
        return `${name} works with ${expert}, but ${productTitle} targets ${productSide}.`;
    }
    if (o.attrSlug === "user-tier") {
        return `Skill level mismatch: ${name} is tagged ${expert}, ${productTitle} targets ${productSide}.`;
    }
    if (o.attrSlug === "gender-focus") {
        return `Gender focus differs: ${expert} vs ${productSide}.`;
    }
    return `${o.attrName}: ${expert} vs ${productSide}.`;
}

/**
 * Plain-English narrative for the Algo chip's tooltip.
 * Names the highest-weight categories that went to zero — those are usually
 * the dominant reason for a low score.
 */
function explainScore({ expertName, productTitle, score, breakdown, professions }) {
    const name = expertName || "This expert";
    const product = productTitle ? `"${productTitle}"` : "this product";
    const primary = pickPrimaryProfession(professions);

    if (score === null || score === undefined) {
        return `${name}'s match score for ${product} hasn't been computed yet. The recompute is running in the background — refresh in a few minutes.`;
    }
    // Search-pool case: the score is known but the per-attribute breakdown
    // isn't loaded (the lightweight search endpoint returns scores only).
    if (!breakdown) {
        return `${name} scores ${Math.round(score * 100)}% for ${product}.`;
    }
    const hasAnySubProfession = (professions || []).some((p) => p.subProfessionName);
    if (!hasAnySubProfession) {
        return `${name}'s profession is recorded as "${primary?.professionName || "unknown"}" but no specific sub-profession is set, so there's nothing to compare against ${product}.`;
    }

    const pct = Math.round(score * 100);
    const tier = pct >= 60 ? "strong" : pct >= 25 ? "partial" : "weak";
    const label = professionLabel(primary);
    const tenurePhrase = primary
        ? `${formatYears(primary.years)} as ${primary.subProfessionName ? `a ${label}` : `a ${primary.professionName} professional`}`
        : null;

    const overlaps = breakdown.overlaps || [];
    const zeroOverlaps = overlaps
        .filter((o) => o.productValues.length > 0 && o.overlap === 0)
        .sort((a, b) => (b.weight || 0) - (a.weight || 0));
    const partialOverlaps = overlaps
        .filter((o) => o.productValues.length > 0 && o.overlap > 0 && o.overlap < 0.99)
        .sort((a, b) => (b.weight || 0) - (a.weight || 0));
    const fullOverlaps = overlaps.filter((o) => o.productValues.length > 0 && o.overlap >= 0.99);

    const parts = [`Algo ${pct}% — A × Σ(W·E).`];
    if (tenurePhrase) parts.push(`${name} has ${tenurePhrase}.`);

    if (zeroOverlaps.length > 0) {
        const top = zeroOverlaps.slice(0, 2);
        parts.push(top.map((o) => attrMismatchSentence(name, product, o)).join(" "));
        if (zeroOverlaps.length > 2) {
            parts.push(`${zeroOverlaps.length - 2} other categor${zeroOverlaps.length - 2 === 1 ? "y" : "ies"} also don't match.`);
        }
    }

    if (fullOverlaps.length > 0 && tier !== "strong") {
        const names = fullOverlaps.slice(0, 3).map((o) => o.attrName.toLowerCase());
        parts.push(`The ${joinHuman(names)} tags do match.`);
    } else if (fullOverlaps.length > 0 && fullOverlaps.length === overlaps.filter((o) => o.productValues.length > 0).length) {
        parts.push(`Every targeted category lines up.`);
    } else if (partialOverlaps.length > 0 && zeroOverlaps.length === 0) {
        const partialNames = partialOverlaps.slice(0, 2).map((o) => o.attrName.toLowerCase());
        parts.push(`Partial overlap on ${joinHuman(partialNames)} keeps Jaccard below 100%.`);
    }

    if (tier === "weak" && primary && primary.years < 0.5) {
        parts.push(`Tenure is also very short, which keeps experience signal low.`);
    } else if (tier === "strong") {
        parts.push(`Overall a strong fit.`);
    }

    return parts.join(" ");
}

const ALGO_INFO = (
    <span style={{ fontSize: 12, lineHeight: 1.5 }}>
        <strong>How the algorithm scores experts</strong>
        <br />
        SCORE = A × Σ(W·E)
        <br />
        <br />
        <strong>A</strong> — weighted Jaccard alignment across attributes the product targets
        (Age, Tier, Function, etc.). Each parent attribute has an admin-set weight.
        <br />
        <strong>W</strong> — normalized profession relevance. Multi-profession experts get more credit on the more-relevant ones.
        <br />
        <strong>E</strong> — experience curve per profession: 1 − e^(−years/5). Diminishing returns past ~5 years.
        <br />
        <br />
        Jaccard is symmetric: a specialist with a narrower vector than the product targets is capped well below 100%.
    </span>
);

// ---------------------------------------------------------------------------
// Industry / Use-Case classification badge + Industry-lens chip
// ---------------------------------------------------------------------------

/**
 * Plain-English narrative for the Industry/Use-Case badge tooltip, built from
 * the classification signals of the expert's best product entry.
 */
function explainExpertType({ expertName, isIndustry, entries }) {
    const name = expertName || "This expert";
    const parts = [];
    // Prefer an industry-labeled entry's signals; fall back to any entry.
    const best = entries.find((e) => e && e.type === "industry") || entries.find(Boolean);
    if (isIndustry) {
        parts.push(`${name} classifies as an Industry Expert for this project — a credentialed professional in the product's field (peer-level authority for a core professional audience).`);
        const quals = (best?.signals?.qualifyingProfessions || []).slice(0, 3);
        if (quals.length > 0) {
            const items = quals.map((q) => {
                const why = [
                    q.professionalTier ? "professional-tier" : null,
                    q.regulated ? "regulated field" : null,
                ].filter(Boolean).join(", ");
                return `${q.name || "a matching profession"} (${formatYears(q.years)}${why ? `, ${why}` : ""})`;
            });
            parts.push(`Qualifies via ${joinHuman(items)}.`);
        }
    } else {
        parts.push(`${name} classifies as a Use-Case Expert for this project — a credible practitioner who uses this kind of product in real life (reach into adjacent audiences).`);
        const s = best?.signals;
        if (s) {
            if (s.relevantProfessions === 0) {
                parts.push(`None of their professions align with the project's products.`);
            } else if (!s.professionalTier && !s.regulated) {
                parts.push(`Their matching professions are enthusiast/consumer-tier rather than professional-tier or regulated.`);
            } else if (s.bestTenureYears < s.minYears) {
                parts.push(`They match a professional-tier field but tenure (${formatYears(s.bestTenureYears)}) is under the ${s.minYears}-year bar.`);
            }
        }
    }
    parts.push(`Classification reads the same signals the Industry lens weights (User Tier, Compliance/Risk, tenure), so badge and I-score always agree.`);
    return parts.join(" ");
}

const INDUSTRY_LENS_INFO =
    "Industry lens (I) — the same scoring kernel run with a second, authority-weighted attribute vector: User Tier, Skill/Qualification and Compliance/Risk boosted; context attributes (Environment, Use Setting, Frequency…) damped. High I = credentialed professional in this product's field. High Algo + low I = strong use-case fit without professional credentials. Computed live from admin-tunable weights (Expert IQ → Attribute Weights), no recompute needed.";

/** Small Industry/Use-Case pill shown once per expert (project-level). */
function ExpertTypeBadge({ expertName, expertTypes, products }) {
    const entries = products.map((p) => expertTypes[p.desireid]).filter(Boolean);
    if (entries.length === 0) return null;
    const isIndustry = entries.some((e) => e.type === "industry");
    const sentence = explainExpertType({ expertName, isIndustry, entries });
    // Classes rather than an inline style so dark can differ: these are pale
    // washes built for a white card, and on the dark canvas they read as bright
    // blobs with near-invisible ink. Light keeps the exact original values.
    // "Use-Case" is the neutral / no-strong-signal state, so dark gives it the
    // surface tokens instead of a hue.
    const chipCls = isIndustry
        ? "bg-[#E4EDF6] text-[#4A7BA6] dark:bg-[#1F4E79]/35 dark:text-[#9CC2E8]"
        : "bg-[#F2EEE8] text-[#9B958C] dark:bg-surface-2 dark:text-text-2";
    return (
        <Tooltip placement="top" arrow title={<span style={{ fontSize: 12, lineHeight: 1.45 }}>{sentence}</span>}>
            <span
                className={`text-[9px] font-bold uppercase tracking-[0.6px] font-circular rounded-[6px] h-[16px] px-[5px] inline-flex items-center shrink-0 cursor-help ${chipCls}`}
            >
                {isIndustry ? "Industry" : "Use-Case"}
            </span>
        </Tooltip>
    );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AlgoScorePerProduct = ({
    products = [],
    scores = {},
    breakdowns = {},
    industryScores = {},
    expertTypes = {},
    expertName = "",
    professions = [],
    className = "",
}) => {
    const { role } = useUser();
    if (!isInternal(role)) return null;
    if (!products.length) return null;

    const hasLens = products.some((p) => {
        const v = industryScores[p.desireid];
        return v !== null && v !== undefined && Number.isFinite(Number(v));
    });

    return (
        <div
            className={`flex flex-wrap items-center gap-[6px] min-w-0 ${className}`}
            aria-label="Algorithm-suggested match per product"
        >
            <span className="text-[9px] font-semibold uppercase tracking-[0.6px] text-text-2 font-circular shrink-0">
                Algo
            </span>
            <Tooltip placement="top" arrow title={ALGO_INFO}>
                <span className="inline-flex items-center cursor-help text-text-2 shrink-0">
                    <IconInfo width={12} height={12} stroke="currentColor" />
                </span>
            </Tooltip>
            <ExpertTypeBadge expertName={expertName} expertTypes={expertTypes} products={products} />
            {products.map((product) => {
                const raw = scores[product.desireid];
                const has = raw !== null && raw !== undefined && Number.isFinite(Number(raw));
                const pct = has ? Math.round(Number(raw) * 100) : null;
                const bd = breakdowns[product.desireid];
                // Colors map to affinity bands: ≥75 green (uses this exact product
                // category), 60–74 darker-orange (uses the family), 40–59 orange
                // (uses the broad area), <40 red (context-only / no product-use fit).
                // Light values are unchanged; each band gains a dark variant that
                // is a translucent wash of its OWN hue plus a light ink, so the
                // affinity banding still reads at a glance on the dark canvas.
                let chipCls = "bg-[#F2EEE8] text-[#9B958C] dark:bg-surface-2 dark:text-text-2";
                if (pct !== null) {
                    if (pct >= 75) chipCls = "bg-[#E6F0EA] text-[#5A9E6F] dark:bg-up-bg dark:text-up";
                    else if (pct >= 60) chipCls = "bg-[#EEE7DA] text-[#917A45] dark:bg-[#7A5B12]/30 dark:text-[#E0BE73]";
                    else if (pct >= 40) chipCls = "bg-[#F4ECDD] text-[#AB854A] dark:bg-tint dark:text-accent-text";
                    else chipCls = "bg-[#FBE9E2] text-[#D4725C] dark:bg-down-bg dark:text-down";
                }
                const sentence = explainScore({
                    expertName,
                    productTitle: product.title,
                    score: has ? Number(raw) : null,
                    breakdown: bd,
                    professions,
                });
                const rawLens = industryScores[product.desireid];
                const hasLensScore = rawLens !== null && rawLens !== undefined && Number.isFinite(Number(rawLens));
                const lensPct = hasLensScore ? Math.round(Number(rawLens) * 100) : null;
                const lensSentence = hasLensScore
                    ? `Industry lens ${lensPct}% for "${product.title}". ${INDUSTRY_LENS_INFO}`
                    : INDUSTRY_LENS_INFO;
                return (
                    <span key={product.desireid} className="inline-flex items-center gap-[3px] shrink-0">
                        <Tooltip
                            placement="top"
                            arrow
                            title={<span style={{ fontSize: 12, lineHeight: 1.45 }}>{sentence}</span>}
                        >
                            <div
                                className={`border border-border-token rounded-[8px] h-[22px] px-[6px] flex items-center gap-[3px] shrink-0 cursor-help ${chipCls}`}
                            >
                                {/* both spans inherit the band colour from the chip */}
                                <span className="text-[11px] font-bold font-circular leading-none">
                                    {pct === null ? "—" : pct}
                                </span>
                                {pct !== null && (
                                    <span className="text-[8px] font-semibold font-circular leading-none">
                                        %
                                    </span>
                                )}
                            </div>
                        </Tooltip>
                        {hasLens && (
                            <Tooltip
                                placement="top"
                                arrow
                                title={<span style={{ fontSize: 12, lineHeight: 1.45 }}>{lensSentence}</span>}
                            >
                                <div
                                    className={`border rounded-[8px] h-[22px] px-[5px] flex items-center gap-[2px] shrink-0 cursor-help border-[#DCE5EE] dark:border-border-token ${
                                        lensPct !== null && lensPct >= 60
                                            ? "bg-[#E4EDF6] dark:bg-[#1F4E79]/35"
                                            : "bg-[#F0F3F6] dark:bg-surface-2"
                                    }`}
                                >
                                    <span className="text-[8px] font-semibold font-circular leading-none text-[#7B93AB] dark:text-text-2">
                                        I
                                    </span>
                                    <span
                                        className={`text-[11px] font-bold font-circular leading-none ${
                                            lensPct !== null && lensPct >= 60
                                                ? "text-[#4A7BA6] dark:text-[#9CC2E8]"
                                                : "text-[#8DA2B8] dark:text-text-2"
                                        }`}
                                    >
                                        {lensPct === null ? "—" : lensPct}
                                    </span>
                                </div>
                            </Tooltip>
                        )}
                    </span>
                );
            })}
        </div>
    );
};

export default AlgoScorePerProduct;
